import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BrandLogo from '../components/BrandLogo';
import { useScrolled } from '../components/motion';
import { PRODUCTS } from './productsData';
import { FREE_TOOLS } from './freeToolsData';

const SIMPLE_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/results', label: 'Results' },
  { to: '/about', label: 'About' },
];

function NavAuthLink() {
  const { user } = useAuth();
  if (user) return <Link to="/dashboard" className="nav-auth-btn">{user.name.split(' ')[0]}</Link>;
  return <Link to="/login" className="nav-auth-btn">Sign In</Link>;
}

function NavDropdown({ id, label, to, items, viewAll, openId, setOpenId }) {
  const ref = useRef(null);
  const open = openId === id;

  useEffect(() => {
    if (!open) return undefined;
    const onClick = event => { if (ref.current && !ref.current.contains(event.target)) setOpenId(null); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, setOpenId]);

  return (
    <div
      className={`nav-dd${open ? ' is-open' : ''}`}
      ref={ref}
      onMouseEnter={() => setOpenId(id)}
      onMouseLeave={() => setOpenId(current => (current === id ? null : current))}
    >
      <button type="button" className="nav-dd-trigger" aria-expanded={open} aria-haspopup="true" onClick={() => setOpenId(open ? null : id)}>
        {label}<ChevronDown size={14} />
      </button>
      <div className="nav-dd-panel" role="menu">
        <div className="nav-dd-list">
          {items.map(item => {
            const Icon = item.icon;
            return (
              <Link key={item.name} to={item.to} className="nav-dd-item" role="menuitem" onClick={() => setOpenId(null)}>
                <span className="nav-dd-icon"><Icon size={17} /></span>
                <span className="nav-dd-copy"><strong>{item.name}</strong><small>{item.tagline}</small></span>
              </Link>
            );
          })}
        </div>
        <Link to={to} className="nav-dd-all" onClick={() => setOpenId(null)}>{viewAll} <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
}

export default function MarketingLayout() {
  const navRef = useScrolled(24);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const productItems = PRODUCTS.map(product => ({ to: `/products/${product.slug}`, name: product.name, tagline: product.tagline, icon: product.icon }));
  const toolItems = FREE_TOOLS.slice(0, 6).map(tool => ({
    to: tool.kind === 'tool' ? `/free-tools/${tool.slug}` : tool.kind === 'account' ? '/dashboard' : '/free-tools#visibility-scan',
    name: tool.name, tagline: tool.tagline, icon: tool.icon,
  }));

  useEffect(() => { setMenuOpen(false); setOpenId(null); }, [location.pathname]);
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <div className="app">
      <nav className="nav" ref={navRef}>
        <div className="nav-logo">
          <Link to="/" aria-label="Data Tallied home"><BrandLogo className="nav-brand-logo" /></Link>
        </div>

        <div className="nav-links">
          <NavDropdown id="products" label="Products" to="/products" viewAll="View all products" items={productItems} openId={openId} setOpenId={setOpenId} />
          <NavLink to="/pricing" className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Pricing</NavLink>
          <NavLink to="/results" className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Results</NavLink>
          <NavDropdown id="free-tools" label="Free Tools" to="/free-tools" viewAll="View all free tools" items={toolItems} openId={openId} setOpenId={setOpenId} />
          <NavLink to="/about" className={({ isActive }) => (isActive ? 'is-active' : undefined)}>About</NavLink>
          <NavAuthLink />
          <button type="button" className="nav-menu-btn" onClick={() => setMenuOpen(value => !value)} aria-expanded={menuOpen} aria-label={menuOpen ? 'Close menu' : 'Open menu'}>
            {menuOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>

        {menuOpen && (
          <div className="nav-mobile-panel">
            <NavLink to="/products" className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Products</NavLink>
            {SIMPLE_LINKS.map(link => <NavLink key={link.to} to={link.to} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>{link.label}</NavLink>)}
            <NavLink to="/free-tools" className={({ isActive }) => (isActive ? 'is-active' : undefined)}>Free Tools</NavLink>
            <Link to="/dashboard">Dashboard</Link>
          </div>
        )}
      </nav>

      <Outlet />

      <footer className="footer" id="contact">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <BrandLogo className="footer-brand-logo" variant="icon" />
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <div className="footer-col-title">Product</div>
                <Link to="/products">Platform overview</Link>
                <Link to="/products#ai-studio">AI Studio</Link>
                <Link to="/products#local-visibility">Local Rank Tracking</Link>
                <Link to="/products#ventures">Full tool stack</Link>
                <Link to="/pricing">Plans</Link>
              </div>
              <div className="footer-col">
                <div className="footer-col-title">Explore</div>
                <Link to="/free-tools">Free Tools</Link>
                <Link to="/results">Results</Link>
                <Link to="/about">About</Link>
                <Link to="/signup">Create account</Link>
              </div>
              <div className="footer-col">
                <div className="footer-col-title">Developers</div>
                <Link to="/docs/datatallied-api">API Reference</Link>
                <Link to="/docs/datatallied-api#webhooks">Webhooks</Link>
                <Link to="/docs/datatallied-api#export">Data Export</Link>
              </div>
              <div className="footer-col">
                <div className="footer-col-title">Setup Guides</div>
                <a href="mailto:host@loadedloaded.com?subject=How%20to%20Add%20GBP%20Access">How to Add GBP Access</a>
                <a href="mailto:host@loadedloaded.com?subject=How%20to%20Add%20FB%20Access">How to Add FB Access</a>
                <a href="mailto:host@loadedloaded.com?subject=How%20to%20Upload%20Media">How to Upload Media</a>
              </div>
            </div>
          </div>

          <div className="footer-support">
            <div className="pe-left">
              <div className="pe-tag">Support</div>
              <div className="pe-title">Have a question or need help?</div>
              <div className="pe-desc">Send account, setup, integration, or plan questions directly to the Data Tallied team.</div>
            </div>
            <a href="mailto:host@loadedloaded.com" className="plan-btn plan-btn-outline pe-cta">Talk to support →</a>
          </div>

          <div className="footer-bottom">
            <span className="footer-copy">© 2026 Data Tallied. All rights reserved.</span>
            <div className="footer-badge">
              <span className="badge-dot" />
              Based in FL
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
